/**
 * Os documentos do HUB DP. PUROS: entram os dados, sai texto — HTML para os que
 * vão a papel, texto simples para os que vão a outro sistema.
 *
 * Quem abre a janela e chama `print()` é a tela, porque isso é IO. A divisão é a
 * de `resumoHonorarios.ts` e `documentosMei.ts`, pelo mesmo motivo: assim os
 * documentos têm prova sem precisar de navegador.
 *
 * ---------------------------------------------------------------------------
 * Três vão a PAPEL, dois vão a OUTRO SISTEMA — e por isso são diferentes
 * ---------------------------------------------------------------------------
 * Na origem os cinco saíam iguais: texto ASCII num `<pre>` monoespaçado, com a
 * única saída sendo o botão "Copiar". Nenhum imprimia — e a tela de Férias
 * anunciava "aviso pronto para impressão" sem entregar.
 *
 * Victor decidiu (24/08) separar pelo DESTINO do documento:
 *
 *  · **recibo de holerite, aviso de férias e contrato CLT** vão para a mão de uma
 *    pessoa, para assinar e arquivar. Viram folha imprimível (`window.print()` +
 *    `@media print`), como o resumo de honorários e o currículo já fazem aqui.
 *  · **XML do eSocial e resumo de guias** vão para um validador e para uma
 *    planilha. Seguem TEXTO, porque imprimir XML não serve para nada — ele
 *    existe para ser colado.
 *
 * O alinhamento por contagem de caracteres da origem morreu com a mudança: ele
 * só alinhava em fonte monoespaçada, e colado no Word com fonte proporcional as
 * assinaturas saíam tortas.
 *
 * ---------------------------------------------------------------------------
 * Por que NÃO há marca d'água "FICTÍCIO" aqui, e no Simulador MEI há
 * ---------------------------------------------------------------------------
 * A doutrina da casa é que tela imitando sistema de terceiro pede o inverso na
 * mesma medida — e os três documentos do Simulador MEI levam marca d'água,
 * porque imitam documento OFICIAL do governo (CCMEI, DAS) com fidelidade visual.
 * Um papel que parece emitido pela Receita e não é, é um problema.
 *
 * Aqui é o contrário: recibo de holerite, aviso de férias e contrato de trabalho
 * são documentos PRIVADOS, da empresa para o empregado. Não imitam papel de
 * autoridade nenhuma, e o aluno — que é contador — vai usar com cliente real. Uma
 * marca d'água "DOCUMENTO FICTÍCIO" tornaria o documento inútil justamente para o
 * uso legítimo.
 *
 * O que fica, e não é configurável, é o rodapé dizendo que o documento foi gerado
 * por ferramenta educacional e deve ser conferido antes de assinar. Isso não
 * invalida o papel; avisa quem o produziu.
 *
 * ---------------------------------------------------------------------------
 * A cor da folha é FIXA, e isso é a doutrina, não exceção a ela
 * ---------------------------------------------------------------------------
 * A tela do hub troca de tema; um documento não. Ele vai ser impresso, salvo em
 * PDF e mandado a outra pessoa — então preto sobre branco nas duas pontas. Cor
 * semântica dentro do documento o faria mudar de aparência conforme o tema de
 * quem o gerou, o que é absurdo. A moldura em volta (a tela) usa tokens; a folha,
 * não.
 *
 * ---------------------------------------------------------------------------
 * Nada de `new Date()` aqui dentro
 * ---------------------------------------------------------------------------
 * Data de emissão e competência entram como PARÂMETRO, já formatados. Função que
 * lê o relógio não tem prova estável: cada chamada devolve outra coisa.
 */

// ---------------------------------------------------------------------------
// Escape
// ---------------------------------------------------------------------------

/**
 * Escape de HTML. Todo valor que o usuário digitou passa por aqui.
 *
 * O hub não tem uma única ocorrência de `dangerouslySetInnerHTML`, e a marcação
 * própria do `TextoRico` existe para não abrir a primeira exceção. Um documento
 * educacional não é motivo para abrir: o nome da empresa e do empregado são
 * digitados pelo aluno, e um `<script>` interpolado cru rodaria com a sessão dele.
 */
export function escaparHtml(v: string | null | undefined): string {
  return String(v ?? "")
    .split("&").join("&amp;")
    .split("<").join("&lt;")
    .split(">").join("&gt;")
    .split('"').join("&quot;")
    .split("'").join("&#39;");
}

/**
 * Escape de XML, que NÃO é o mesmo de HTML.
 *
 * XML exige `&apos;` — `&#39;` é válido, mas o `&quot;`/`&apos;` nomeados são os
 * cinco predefinidos da especificação, e validador estrito de esquema reclama do
 * resto. E aqui a consequência de errar é concreta: o aluno cola o XML no
 * validador do eSocial e recebe erro de parsing, achando que a ferramenta gerou
 * evento inválido — quando o que houve foi um `&` no nome da empresa.
 */
export function escaparXml(v: string | null | undefined): string {
  return String(v ?? "")
    .split("&").join("&amp;")
    .split("<").join("&lt;")
    .split(">").join("&gt;")
    .split('"').join("&quot;")
    .split("'").join("&apos;");
}

/** Só dígitos — para CPF/CNPJ/PIS dentro do XML, que não aceita máscara. */
export const soDigitos = (v: string | null | undefined): string =>
  String(v ?? "").replace(/\D+/g, "");

// ---------------------------------------------------------------------------
// A folha
// ---------------------------------------------------------------------------

/**
 * O CSS das folhas. Cor FIXA — ver o cabeçalho.
 *
 * `.acoes` desaparece na impressão: o botão "Imprimir" não vai para o papel.
 * `@page` com margem de 1,5cm porque documento de RH é arquivado em pasta, e
 * margem menor faz o furador comer texto.
 */
const ESTILO_FOLHA = `
  @page { size: A4; margin: 1.5cm }
  * { box-sizing: border-box }
  body {
    margin: 0; padding: 24px;
    background: #eef0f4;
    font: 14px/1.5 "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #111827;
  }
  .folha {
    max-width: 780px; margin: 0 auto; padding: 40px;
    background: #ffffff; color: #111827;
    border: 1px solid #d1d5db;
  }
  .acoes { max-width: 780px; margin: 0 auto 16px; text-align: right }
  .acoes button {
    font: 600 14px/1 inherit; padding: 10px 18px; cursor: pointer;
    background: #ff9933; color: #102850; border: 0; border-radius: 999px;
  }
  h1 { font-size: 19px; margin: 0 0 4px; letter-spacing: .02em }
  h2 { font-size: 14px; margin: 26px 0 8px; text-transform: uppercase; letter-spacing: .06em; color: #102850 }
  .cabecalho { border-bottom: 3px solid #102850; padding-bottom: 12px; margin-bottom: 8px }
  .sub { font-size: 12px; color: #4b5563 }
  table { width: 100%; border-collapse: collapse; margin: 8px 0 }
  th, td { padding: 7px 8px; border-bottom: 1px solid #e5e7eb; text-align: left; vertical-align: top }
  th { font-size: 11px; text-transform: uppercase; letter-spacing: .05em; color: #4b5563; font-weight: 600 }
  td.n, th.n { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap }
  tr.total td { border-top: 2px solid #102850; border-bottom: 0; font-weight: 700; padding-top: 10px }
  .k { color: #4b5563; width: 42% }
  p { margin: 0 0 10px; text-align: justify }
  ol { padding-left: 22px } ol li { margin-bottom: 10px; text-align: justify }
  .assinaturas { margin-top: 44px; display: flex; gap: 32px }
  .assinaturas div { flex: 1; border-top: 1px solid #111827; padding-top: 6px; font-size: 12px; text-align: center }
  .rodape { margin-top: 32px; padding-top: 10px; border-top: 1px solid #e5e7eb; font-size: 10.5px; color: #6b7280; text-align: center }
  @media print {
    .acoes { display: none }
    body { background: #fff; padding: 0 }
    .folha { border: 0; padding: 0; max-width: none }
  }
`;

/**
 * O rodapé de toda folha. Não é configurável — ver o cabeçalho do módulo.
 *
 * Diz o que precisa ser dito (documento gerado por ferramenta educacional,
 * confira antes de assinar) sem invalidar o papel, que é o uso legítimo.
 */
const RODAPE =
  "Documento gerado pelo HUB DP &middot; Contabilidade Facilitada &mdash; ferramenta educacional. " +
  "Confira os valores e as cláusulas antes de assinar; não substitui a análise de um profissional.";

function folha(titulo: string, corpo: string): string {
  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escaparHtml(titulo)}</title>
<style>${ESTILO_FOLHA}</style>
</head>
<body>
<div class="acoes"><button type="button" onclick="window.print()">Imprimir ou salvar em PDF</button></div>
<div class="folha">
${corpo}
<div class="rodape">${RODAPE}</div>
</div>
</body>
</html>`;
}

/** Uma linha de valor da tabela. `destaque` engrossa (totais). */
const linha = (rotulo: string, valor: string, destaque = false): string =>
  `<tr${destaque ? ' class="total"' : ""}><td>${escaparHtml(rotulo)}</td><td class="n">${escaparHtml(valor)}</td></tr>`;

// ---------------------------------------------------------------------------
// 1. Recibo de holerite
// ---------------------------------------------------------------------------

export interface DadosRecibo {
  empresa: string;
  cnpj: string;
  empregado: string;
  cpf: string;
  cargo: string;
  admissao: string;
  competencia: string;
  dataEmissao: string;
  /** Rótulo + valor formatado, já em ordem. */
  proventos: Array<{ rotulo: string; valor: string }>;
  descontos: Array<{ rotulo: string; valor: string }>;
  totalProventos: string;
  totalDescontos: string;
  liquido: string;
  baseInss: string;
  baseIrrf: string;
  fgts: string;
  /** De que ano são as tabelas usadas — vem do banco, não é cravado. */
  referencia: string;
}

/**
 * O recibo de pagamento de salário.
 *
 * Os itens vêm já formatados e em ORDEM, e a função não decide quais aparecem:
 * quem monta a lista é a tela, a partir do resultado do cálculo. Se a função
 * filtrasse "mostrar só o que é diferente de zero", ela passaria a ter regra
 * dentro — e a decisão de mostrar ou esconder uma verba é de apresentação.
 *
 * As bases de INSS e IRRF e o FGTS aparecem em bloco separado porque é assim que
 * o holerite legal se apresenta (Portaria 3.626/91): o FGTS **não é desconto** e
 * não pode figurar entre eles, e essa é a confusão mais comum de quem está
 * aprendendo.
 */
export function reciboHolerite(d: DadosRecibo): string {
  const corpo = `
<div class="cabecalho">
  <h1>${escaparHtml(d.empresa) || "&mdash;"}</h1>
  <div class="sub">CNPJ ${escaparHtml(d.cnpj) || "&mdash;"} &middot; Recibo de pagamento de salário</div>
</div>
<div class="sub">Competência ${escaparHtml(d.competencia)} &middot; Emitido em ${escaparHtml(d.dataEmissao)}</div>

<h2>Empregado</h2>
<table>
  <tr><td class="k">Nome</td><td><b>${escaparHtml(d.empregado) || "&mdash;"}</b></td></tr>
  <tr><td class="k">CPF</td><td>${escaparHtml(d.cpf) || "&mdash;"}</td></tr>
  <tr><td class="k">Cargo</td><td>${escaparHtml(d.cargo) || "&mdash;"}</td></tr>
  <tr><td class="k">Admissão</td><td>${escaparHtml(d.admissao) || "&mdash;"}</td></tr>
</table>

<h2>Proventos</h2>
<table>
  <tr><th>Descrição</th><th class="n">Valor</th></tr>
  ${d.proventos.map((p) => linha(p.rotulo, p.valor)).join("\n  ")}
  ${linha("Total de proventos", d.totalProventos, true)}
</table>

<h2>Descontos</h2>
<table>
  <tr><th>Descrição</th><th class="n">Valor</th></tr>
  ${d.descontos.length > 0 ? d.descontos.map((x) => linha(x.rotulo, x.valor)).join("\n  ") : '<tr><td colspan="2">Nenhum desconto na competência.</td></tr>'}
  ${linha("Total de descontos", d.totalDescontos, true)}
</table>

<table>
  ${linha("Líquido a receber", d.liquido, true)}
</table>

<h2>Bases e depósito</h2>
<table>
  <tr><td class="k">Base do INSS</td><td class="n">${escaparHtml(d.baseInss)}</td></tr>
  <tr><td class="k">Base do IRRF</td><td class="n">${escaparHtml(d.baseIrrf)}</td></tr>
  <tr><td class="k">FGTS depositado <span class="sub">(depósito do empregador, não é desconto)</span></td><td class="n">${escaparHtml(d.fgts)}</td></tr>
</table>
<p class="sub">Tabelas aplicadas: ${escaparHtml(d.referencia)}</p>

<div class="assinaturas">
  <div>${escaparHtml(d.empresa) || "Empregador"}</div>
  <div>${escaparHtml(d.empregado) || "Empregado"}</div>
</div>
<p class="sub" style="margin-top:16px">Declaro ter recebido a importância líquida discriminada neste recibo.</p>`;
  return folha(`Recibo de holerite — ${d.empregado || "empregado"}`, corpo);
}

// ---------------------------------------------------------------------------
// 2. Aviso de férias
// ---------------------------------------------------------------------------

export interface DadosAviso {
  empresa: string;
  cnpj: string;
  cidade: string;
  empregado: string;
  cpf: string;
  cargo: string;
  periodoAquisitivo: string;
  inicioGozo: string;
  fimGozo: string;
  retorno: string;
  diasGozo: number;
  diasVendidos: number;
  adiantaDecimo: boolean;
  dataEmissao: string;
}

/**
 * O aviso de férias (CLT art. 135).
 *
 * O prazo de 30 dias de antecedência está no texto porque é a razão de o
 * documento existir: avisar depois disso não cumpre o artigo, e o empregado pode
 * recusar as férias na data marcada.
 */
export function avisoFerias(d: DadosAviso): string {
  const abono =
    d.diasVendidos > 0
      ? `<p>Do período acima, <b>${d.diasVendidos} dia(s)</b> foram convertidos em abono pecuniário, conforme o art. 143 da CLT, a pedido do empregado.</p>`
      : "";
  const decimo = d.adiantaDecimo
    ? "<p>Nesta oportunidade será paga a primeira parcela do 13º salário, conforme o art. 2º, §2º, da Lei nº 4.749/1965.</p>"
    : "";

  const corpo = `
<div class="cabecalho">
  <h1>Aviso de férias</h1>
  <div class="sub">${escaparHtml(d.empresa) || "&mdash;"} &middot; CNPJ ${escaparHtml(d.cnpj) || "&mdash;"}</div>
</div>

<p style="margin-top:18px">
  Comunicamos a <b>${escaparHtml(d.empregado) || "&mdash;"}</b>, CPF ${escaparHtml(d.cpf) || "&mdash;"},
  ocupante do cargo de ${escaparHtml(d.cargo) || "&mdash;"}, que suas férias relativas ao período aquisitivo
  de ${escaparHtml(d.periodoAquisitivo) || "&mdash;"} serão concedidas conforme o quadro abaixo.
</p>

<table>
  <tr><td class="k">Início do gozo</td><td><b>${escaparHtml(d.inicioGozo) || "&mdash;"}</b></td></tr>
  <tr><td class="k">Fim do gozo</td><td><b>${escaparHtml(d.fimGozo) || "&mdash;"}</b></td></tr>
  <tr><td class="k">Retorno ao trabalho</td><td><b>${escaparHtml(d.retorno) || "&mdash;"}</b></td></tr>
  <tr><td class="k">Dias de gozo</td><td>${d.diasGozo}</td></tr>
</table>
${abono}
${decimo}

<p>
  O pagamento das férias e do respectivo terço constitucional será efetuado até 2 (dois) dias antes
  do início do período de gozo, conforme o art. 145 da CLT.
</p>
<p>
  Este aviso é dado com a antecedência mínima de 30 (trinta) dias exigida pelo art. 135 da CLT.
</p>

<p class="sub" style="margin-top:26px">${escaparHtml(d.cidade) || "&mdash;"}, ${escaparHtml(d.dataEmissao)}.</p>

<div class="assinaturas">
  <div>${escaparHtml(d.empresa) || "Empregador"}</div>
  <div>${escaparHtml(d.empregado) || "Ciente — empregado"}</div>
</div>`;
  return folha(`Aviso de férias — ${d.empregado || "empregado"}`, corpo);
}

// ---------------------------------------------------------------------------
// 3. Contrato de trabalho
// ---------------------------------------------------------------------------

export type ModalidadeContrato = "indeterminado" | "experiencia" | "determinado" | "intermitente" | "aprendiz";

export interface DadosContrato {
  empresa: string;
  cnpj: string;
  enderecoEmpresa: string;
  empregado: string;
  cpf: string;
  enderecoEmpregado: string;
  cargo: string;
  salario: string;
  salarioExtenso: string;
  jornada: string;
  local: string;
  inicio: string;
  modalidade: ModalidadeContrato;
  /** Preenchido nas modalidades com prazo. */
  fimPrazo: string;
  /** Dias do período inicial de experiência. */
  diasExperiencia: number;
  cidade: string;
  dataEmissao: string;
  /** Curso e instituição, só no aprendiz. */
  cursoAprendizagem: string;
}

/**
 * A cláusula 2ª muda por modalidade — e é a única que muda.
 *
 * Tabela e não `switch` espalhado: as cinco redações ficam lado a lado, onde se
 * comparam. É a lição do "nome do tema em dois lugares" do Gerador de Currículos.
 */
function clausulaModalidade(d: DadosContrato): string {
  const fim = escaparHtml(d.fimPrazo) || "&mdash;";
  switch (d.modalidade) {
    case "experiencia":
      return `O presente contrato é celebrado a título de <b>experiência</b>, com prazo de
        ${d.diasExperiencia} (${d.diasExperiencia === 1 ? "um" : "vários"}) dias, iniciando-se em
        ${escaparHtml(d.inicio) || "&mdash;"} e encerrando-se em ${fim}, nos termos do art. 443, §2º, "c",
        da CLT. Admite-se uma única prorrogação, desde que a soma dos períodos não exceda
        <b>90 (noventa) dias</b>, sob pena de conversão em contrato por prazo indeterminado
        (art. 445, parágrafo único, da CLT). Não havendo manifestação em contrário até o termo final,
        o contrato passa a vigorar por prazo indeterminado.`;
    case "determinado":
      return `O presente contrato é celebrado por <b>prazo determinado</b>, com início em
        ${escaparHtml(d.inicio) || "&mdash;"} e término em ${fim}, nos termos do art. 443, §2º, da CLT,
        em razão da natureza transitória do serviço. A rescisão antecipada sujeita a parte
        responsável à indenização do art. 479 ou do art. 480 da CLT, conforme o caso.`;
    case "intermitente":
      return `O presente contrato é celebrado na modalidade <b>intermitente</b>, nos termos do
        art. 452-A da CLT. A prestação de serviços não é contínua: o empregador convocará o
        empregado por escrito, com antecedência mínima de 3 (três) dias corridos, indicando a
        jornada a ser cumprida, e o empregado poderá aceitar ou recusar a convocação sem que a
        recusa descaracterize a subordinação. Ao fim de cada período de prestação serão pagas,
        de forma proporcional, a remuneração, as férias com um terço, o 13º salário, o repouso
        semanal remunerado e os adicionais legais.`;
    case "aprendiz":
      return `O presente contrato é de <b>aprendizagem</b>, nos termos do art. 428 da CLT e do
        Decreto nº 9.579/2018, com início em ${escaparHtml(d.inicio) || "&mdash;"} e término em ${fim},
        não excedendo 2 (dois) anos. O empregado está matriculado no curso de
        ${escaparHtml(d.cursoAprendizagem) || "&mdash;"}, e a formação técnico-profissional
        metódica é parte integrante deste contrato. O depósito do FGTS é de <b>2% (dois por cento)</b>
        da remuneração, conforme o art. 15, §7º, da Lei nº 8.036/1990.`;
    default:
      return `O presente contrato é celebrado por <b>prazo indeterminado</b>, com início em
        ${escaparHtml(d.inicio) || "&mdash;"}, regendo-se pelas disposições da Consolidação das Leis do
        Trabalho e pela convenção coletiva aplicável à categoria.`;
  }
}

/** O contrato de trabalho, em nove cláusulas. */
export function contratoTrabalho(d: DadosContrato): string {
  const corpo = `
<div class="cabecalho">
  <h1>Contrato individual de trabalho</h1>
  <div class="sub">Regido pela Consolidação das Leis do Trabalho</div>
</div>

<p style="margin-top:18px">
  <b>EMPREGADOR:</b> ${escaparHtml(d.empresa) || "&mdash;"}, inscrito no CNPJ sob o nº
  ${escaparHtml(d.cnpj) || "&mdash;"}, com sede em ${escaparHtml(d.enderecoEmpresa) || "&mdash;"}.
</p>
<p>
  <b>EMPREGADO:</b> ${escaparHtml(d.empregado) || "&mdash;"}, inscrito no CPF sob o nº
  ${escaparHtml(d.cpf) || "&mdash;"}, residente em ${escaparHtml(d.enderecoEmpregado) || "&mdash;"}.
</p>
<p>As partes acima qualificadas celebram o presente contrato de trabalho, que se regerá pelas cláusulas seguintes.</p>

<ol>
  <li><b>Do objeto.</b> O empregado é admitido para exercer a função de
    <b>${escaparHtml(d.cargo) || "&mdash;"}</b>, obrigando-se a desempenhar as atribuições próprias do cargo
    e as demais compatíveis com sua condição pessoal.</li>

  <li><b>Da modalidade e da vigência.</b> ${clausulaModalidade(d)}</li>

  <li><b>Da remuneração.</b> O empregado perceberá o salário mensal de
    <b>${escaparHtml(d.salario) || "&mdash;"}</b> (${escaparHtml(d.salarioExtenso) || "&mdash;"}),
    pago até o 5º (quinto) dia útil do mês subsequente ao vencido, conforme o art. 459, §1º, da CLT.</li>

  <li><b>Da jornada.</b> A jornada de trabalho será de ${escaparHtml(d.jornada) || "&mdash;"},
    respeitados os limites do art. 58 da CLT e os intervalos legais. As horas extraordinárias, quando
    houver, serão remuneradas com o adicional mínimo de 50% (cinquenta por cento).</li>

  <li><b>Do local de trabalho.</b> Os serviços serão prestados em
    ${escaparHtml(d.local) || "&mdash;"}, podendo o empregador determinar a prestação em outro local
    compatível, observado o art. 469 da CLT.</li>

  <li><b>Dos descontos.</b> O empregador está autorizado a descontar da remuneração as parcelas
    previstas em lei — contribuição previdenciária, imposto de renda retido na fonte e vale-transporte
    no limite de 6% (seis por cento) do salário básico —, além das expressamente autorizadas
    pelo empregado.</li>

  <li><b>Das obrigações do empregado.</b> Cumprir as normas internas e as de segurança e saúde do
    trabalho, usar corretamente os equipamentos de proteção fornecidos, zelar pelos bens que lhe
    forem confiados e guardar sigilo sobre as informações a que tiver acesso.</li>

  <li><b>Da rescisão.</b> O contrato poderá ser rescindido nas hipóteses e com os efeitos previstos
    nos arts. 477 a 484-A da CLT, observados os prazos de aviso prévio e de pagamento das verbas
    rescisórias.</li>

  <li><b>Do foro.</b> Fica eleito o foro da comarca de ${escaparHtml(d.cidade) || "&mdash;"} para
    dirimir as questões oriundas deste contrato, sem prejuízo da competência da Justiça do Trabalho.</li>
</ol>

<p class="sub" style="margin-top:26px">${escaparHtml(d.cidade) || "&mdash;"}, ${escaparHtml(d.dataEmissao)}.</p>

<div class="assinaturas">
  <div>${escaparHtml(d.empresa) || "Empregador"}</div>
  <div>${escaparHtml(d.empregado) || "Empregado"}</div>
</div>
<div class="assinaturas">
  <div>Testemunha 1</div>
  <div>Testemunha 2</div>
</div>`;
  return folha(`Contrato de trabalho — ${d.empregado || "empregado"}`, corpo);
}

// ---------------------------------------------------------------------------
// 4. Resumo de guias (TEXTO — vai para planilha, não para papel)
// ---------------------------------------------------------------------------

export interface DadosGuias {
  competencia: string;
  folhaBruta: string;
  inssRetido: string;
  inssPatronal: string;
  rat: string;
  terceiros: string;
  fgts: string;
  irrfRetido: string;
  totalEncargos: string;
  custoTotal: string;
  vencimentoDctfweb: string;
  vencimentoFgts: string;
  vencimentoPeriodicos: string;
  regime: string;
  /** True quando feriado não entrou no cálculo dos vencimentos. */
  semFeriados: boolean;
}

/**
 * O resumo das guias, em texto.
 *
 * Duas colunas separadas por dois-pontos, sem réguas de `=` nem alinhamento por
 * `padStart`. O original alinhava com contagem de caracteres, o que só funciona
 * em fonte monoespaçada — colado numa planilha ou num e-mail, desmontava. Assim
 * cada linha é um par legível em qualquer fonte, e cola em planilha separando
 * por `:`.
 */
export function resumoGuias(d: DadosGuias): string {
  const linhas = [
    `Resumo de guias — competência ${d.competencia}`,
    `Regime previdenciário: ${d.regime}`,
    "",
    "APURAÇÃO",
    `Folha bruta: ${d.folhaBruta}`,
    `INSS retido dos empregados: ${d.inssRetido}`,
    `IRRF retido: ${d.irrfRetido}`,
    "",
    "ENCARGOS DO EMPREGADOR",
    `INSS patronal: ${d.inssPatronal}`,
    `RAT x FAP: ${d.rat}`,
    `Terceiros: ${d.terceiros}`,
    `FGTS: ${d.fgts}`,
    `Total de encargos: ${d.totalEncargos}`,
    `Custo total da competência: ${d.custoTotal}`,
    "",
    "PRAZOS",
    `Eventos periódicos do eSocial (S-1200/S-1210/S-1299): ${d.vencimentoPeriodicos}`,
    `DCTFWeb e DARF previdenciário: ${d.vencimentoDctfweb}`,
    `FGTS Digital: ${d.vencimentoFgts}`,
    `DARF do IRRF (código 0561): ${d.vencimentoDctfweb}`,
  ];
  if (d.semFeriados) {
    linhas.push(
      "",
      "Atenção: os vencimentos acima recuam de sábado e domingo, mas NÃO consideram feriados.",
      "Confira no calendário oficial se a data cair perto de um feriado nacional ou local.",
    );
  }
  linhas.push(
    "",
    "Gerado pelo HUB DP - Contabilidade Facilitada (ferramenta educacional).",
  );
  return linhas.join("\n");
}

// ---------------------------------------------------------------------------
// 5. XML de evento do eSocial (TEXTO — vai para o validador)
// ---------------------------------------------------------------------------

export type TipoEventoXml = "S-2200" | "S-2299" | "S-1200";

export interface DadosEventoXml {
  tipo: TipoEventoXml;
  /** Versão do leiaute. Vem de fora justamente porque MUDA — ver o comentário. */
  versaoLeiaute: string;
  cnpjEmpregador: string;
  cpfTrabalhador: string;
  nomeTrabalhador: string;
  nascimento: string;
  matricula: string;
  admissao: string;
  desligamento: string;
  motivoDesligamento: string;
  competencia: string;
  cargo: string;
  salario: string;
  /** Identificador do evento. Vem de fora: seria sorteio aqui dentro. */
  idEvento: string;
}

/**
 * O esqueleto XML de um evento do eSocial, para o aluno reconhecer a estrutura.
 *
 * **`versaoLeiaute` entra por PARÂMETRO, e é uma correção sobre a origem.** Lá a
 * versão estava cravada na string do namespace (`v_S_01_03_00`), dentro do
 * componente. Leiaute do eSocial muda por Nota Técnica, e uma versão cravada num
 * `.tsx` é a ferramenta ensinando estrutura obsoleta sem ninguém notar — a mesma
 * classe do `INSS = 81.05` do Simulador. Aqui ela é dado, e a tela mostra qual
 * está usando.
 *
 * `tpAmb=2` é ambiente de TESTES, e não é configurável: um esqueleto didático
 * apontando para produção convidaria ao envio real de evento incompleto.
 */
export function eventoXml(d: DadosEventoXml): string {
  const ns = `http://www.esocial.gov.br/schema/evt/${d.tipo === "S-1200" ? "evtRemun" : d.tipo === "S-2299" ? "evtDeslig" : "evtAdmissao"}/${escaparXml(d.versaoLeiaute)}`;
  const abertura = `<?xml version="1.0" encoding="UTF-8"?>
<!-- Esqueleto DIDÁTICO. Ambiente de testes (tpAmb=2). Para envio real, use o
     portal ou o web service oficial, com assinatura ICP-Brasil. -->
<eSocial xmlns="${escaparXml(ns)}">`;
  const ideEmpregador = `    <ideEmpregador>
      <tpInsc>1</tpInsc>
      <nrInsc>${escaparXml(soDigitos(d.cnpjEmpregador))}</nrInsc>
    </ideEmpregador>`;

  if (d.tipo === "S-2200") {
    return `${abertura}
  <evtAdmissao Id="${escaparXml(d.idEvento)}">
    <ideEvento>
      <indRetif>1</indRetif>
      <tpAmb>2</tpAmb>
      <procEmi>1</procEmi>
      <verProc>HUB-DP-didatico</verProc>
    </ideEvento>
${ideEmpregador}
    <trabalhador>
      <cpfTrab>${escaparXml(soDigitos(d.cpfTrabalhador))}</cpfTrab>
      <nmTrab>${escaparXml(d.nomeTrabalhador)}</nmTrab>
      <sexo>M</sexo>
      <racaCor>1</racaCor>
      <estCiv>1</estCiv>
      <grauInstr>07</grauInstr>
      <nascimento>
        <dtNascto>${escaparXml(d.nascimento)}</dtNascto>
        <paisNascto>105</paisNascto>
        <paisNac>105</paisNac>
      </nascimento>
    </trabalhador>
    <vinculo>
      <matricula>${escaparXml(d.matricula)}</matricula>
      <tpRegTrab>1</tpRegTrab>
      <tpRegPrev>1</tpRegPrev>
      <infoRegimeTrab>
        <infoCeletista>
          <dtAdm>${escaparXml(d.admissao)}</dtAdm>
          <tpAdmissao>1</tpAdmissao>
          <indAdmissao>1</indAdmissao>
          <tpRegJor>1</tpRegJor>
          <natAtividade>1</natAtividade>
        </infoCeletista>
      </infoRegimeTrab>
      <infoContrato>
        <nmCargo>${escaparXml(d.cargo)}</nmCargo>
        <CBOCargo>412105</CBOCargo>
        <remuneracao>
          <vrSalFx>${escaparXml(d.salario)}</vrSalFx>
          <undSalFixo>5</undSalFixo>
        </remuneracao>
        <duracao>
          <tpContr>1</tpContr>
        </duracao>
      </infoContrato>
    </vinculo>
  </evtAdmissao>
</eSocial>`;
  }

  if (d.tipo === "S-2299") {
    return `${abertura}
  <evtDeslig Id="${escaparXml(d.idEvento)}">
    <ideEvento>
      <indRetif>1</indRetif>
      <tpAmb>2</tpAmb>
      <procEmi>1</procEmi>
      <verProc>HUB-DP-didatico</verProc>
    </ideEvento>
${ideEmpregador}
    <ideVinculo>
      <cpfTrab>${escaparXml(soDigitos(d.cpfTrabalhador))}</cpfTrab>
      <matricula>${escaparXml(d.matricula)}</matricula>
    </ideVinculo>
    <infoDeslig>
      <mtvDeslig>${escaparXml(d.motivoDesligamento)}</mtvDeslig>
      <dtDeslig>${escaparXml(d.desligamento)}</dtDeslig>
      <indPagtoAPI>N</indPagtoAPI>
    </infoDeslig>
  </evtDeslig>
</eSocial>`;
  }

  return `${abertura}
  <evtRemun Id="${escaparXml(d.idEvento)}">
    <ideEvento>
      <indRetif>1</indRetif>
      <indApuracao>1</indApuracao>
      <perApur>${escaparXml(d.competencia)}</perApur>
      <tpAmb>2</tpAmb>
      <procEmi>1</procEmi>
      <verProc>HUB-DP-didatico</verProc>
    </ideEvento>
${ideEmpregador}
    <ideTrabalhador>
      <cpfTrab>${escaparXml(soDigitos(d.cpfTrabalhador))}</cpfTrab>
    </ideTrabalhador>
    <dmDev>
      <ideDmDev>${escaparXml(d.matricula)}</ideDmDev>
      <codCateg>101</codCateg>
      <infoPerApur>
        <ideEstabLot>
          <tpInsc>1</tpInsc>
          <nrInsc>${escaparXml(soDigitos(d.cnpjEmpregador))}</nrInsc>
          <codLotacao>001</codLotacao>
          <detVerbas>
            <codRubr>1000</codRubr>
            <ideTabRubr>01</ideTabRubr>
            <vrRubr>${escaparXml(d.salario)}</vrRubr>
          </detVerbas>
        </ideEstabLot>
      </infoPerApur>
    </dmDev>
  </evtRemun>
</eSocial>`;
}
